import { Shell } from '../app/Shell';
import { linkTo } from '../router';
import { CONTACT_EMAIL, LEGAL_UPDATED, OPERATOR } from './SiteFooter';

// /privacy: the Privacy Policy as approved on the canvas (2026-10-27). A change here that matters
// moves LEGAL_UPDATED, and people are emailed first (the policy's own promise).

export function Privacy() {
  const email = <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
  return (
    <Shell home="/">
      <article className="legal">
        <h1>Privacy Policy</h1>
        <p className="quiet">{`Last updated ${LEGAL_UPDATED}`}</p>

        <p>Hearth runs game servers for you and your friends. This page says what we keep about you, why, who else sees it, and how to get it changed or deleted. Short version: we keep what we need to run your servers and bill you, we don't sell it, and we don't show you ads.</p>

        <h2>Who we are</h2>
        <p>Hearth (hearthservers.com) is run by {OPERATOR}, an individual based in Washington, USA ("we"). For anything on this page, use the <a {...linkTo('/support')}>support form</a> or email {email}.</p>

        <h2>What we collect</h2>
        <div className="table-box">
          <table>
            <thead><tr><th style={{ width: '32%' }}>What</th><th>Details</th></tr></thead>
            <tbody>
              <tr><td>Your sign-in</td><td>When you sign in with Google or Discord, they send us your account ID, email address, name, username or display name, and profile picture. If you connect Discord to a Google sign-in, we keep your Discord user ID and username.</td></tr>
              <tr><td>Your servers</td><td>Servers you make or join: their names, settings, game version, who owns them, who joined and through whose invite link, and when.</td></tr>
              <tr><td>Usage and billing</td><td>Each time a server wakes or sleeps, how long it ran, at what size, who woke it, and the fees that come from that. Payments are handled by Stripe: we keep your Stripe customer ID and invoice status, never your card number.</td></tr>
              <tr><td>Your game data</td><td>Worlds you upload and the backups we take of your servers.</td></tr>
              <tr><td>Messages to us</td><td>What you send through the support form or by email.</td></tr>
              <tr><td>Technical logs</td><td>Our hosting provider records requests to our site and API, including IP addresses and times. The game server itself sees the IP address and in-game name of everyone who connects to it, like any server.</td></tr>
            </tbody>
          </table>
        </div>
        <p>We don't use analytics, advertising or tracking tools.</p>

        <h2>Why we use it</h2>
        <ul>
          <li><strong>To run the service you asked for:</strong> signing you in, running and backing up your servers, letting your friends join, showing usage, and charging you (our contract with you).</li>
          <li><strong>To keep Hearth working and safe:</strong> fixing problems, stopping abuse and fraud, and answering you (our legitimate interest in running a reliable service).</li>
          <li><strong>Because the law requires it:</strong> keeping billing and tax records.</li>
        </ul>

        <h2>Who else sees it</h2>
        <p>We don't sell your personal information or share it for advertising. We use these companies to run Hearth, and they only use it to provide their service to us:</p>
        <ul>
          <li><strong>Amazon Web Services</strong>: hosting, storage and email, in the USA (Oregon).</li>
          <li><strong>Stripe</strong>: payments and invoices.</li>
          <li><strong>Google</strong> and <strong>Discord</strong>: signing in, and the Hearth bot if you use it in Discord.</li>
        </ul>
        <p>Other people on Hearth see a little about you: the people on a server you're on see your name and picture, and that you woke or slept it. They never see your email address. We may share information if the law requires it, or to protect people from harm.</p>

        <h2>Your browser</h2>
        <p>We store your sign-in session in your browser so you stay signed in. That's all: no advertising or tracking cookies, so there's nothing to accept.</p>

        <h2>How long we keep it</h2>
        <ul>
          <li>Your account: while you use Hearth. Ask us to delete it at any time (below).</li>
          <li>Backups: the newest 10 of each server. After a server is destroyed, its backups and record are kept until you ask us to delete them.</li>
          <li>Uploads: the file you upload is deleted within 7 days; the world it becomes lives on as your server.</li>
          <li>Billing records: 7 years, for tax law.</li>
          <li>Technical logs: up to 90 days.</li>
          <li>Support messages: up to 2 years after we close them.</li>
        </ul>

        <h2>Your choices and rights</h2>
        <p>Wherever you live, you can ask us to:</p>
        <ul>
          <li>show you a copy of what we hold about you;</li>
          <li>correct it;</li>
          <li>delete your account and data (billing records we must keep stay until the law lets us delete them);</li>
          <li>stop using it for something you object to.</li>
        </ul>
        <p>Send a privacy request through the <a {...linkTo('/support')}>support form</a> or to {email}. We'll answer within 30 days and won't treat you differently for asking. If you're in the EU or UK you can also complain to your data protection authority; California and other US state residents have the rights above under their state laws.</p>

        <h2>Children</h2>
        <p>You must be 13 or older to use Hearth. Players 13 to 17 can join friends' servers; to own a server or add a payment method you must be 18 or older. If we learn we hold information about a child under 13, we delete it. Parents can reach us at the email above.</p>

        <h2>Where your data is</h2>
        <p>Hearth runs in the United States. If you use it from elsewhere, your information is processed in the USA, where privacy law may differ from yours.</p>

        <h2>Security</h2>
        <p>Your data is encrypted in transit and at rest, access to it is limited to what each part of Hearth needs, and sign-in goes through Google or Discord, so we never hold a password of yours. No service is perfectly secure; if a breach affects you, we'll tell you as the law requires.</p>

        <h2>Changes</h2>
        <p>If we change this policy we'll update the date above, and email you before any change that matters.</p>
      </article>
    </Shell>
  );
}
